import { apiDelete, apiPut, apiGet } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import AddRawMaterialElement from '../../generic/smartAddElement/AddRawMaterialElement'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import React, { useEffect, useRef, useState } from 'react'
import { CircularProgress, IconButton } from '@mui/material'
import { useToggle } from '../../generic/hooks/useToggle'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { TextSnippetOutlined } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import Document from '../document/Document'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import SearchIcon from '@mui/icons-material/Search'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QrCodeGeneratorDialog from '../../generic/qr/QrCodeGeneratorDialog'

// ── Pure helpers (exported for unit testing) ──────────────────────────────────
// RN4 / D2: rounding to 3 decimal places (aligned with DB scale 3).

export const roundTo3 = (value) => Math.round(value * 1000) / 1000

// A field value is blank when it is empty, null or undefined. Numeric inputs are
// stored as strings, so '' must be treated as "no value" (Number('') === 0).
export const isBlank = (value) => value === '' || value === null || value === undefined

// Compute qASU from qa: qASU = size * qa (S12, S14)
export const computeQasuFromQa = (size, qa) => roundTo3(size * qa)

// Compute qa from qASU: qa = qasu / size — only call when size > 0 (S13, S17)
export const computeQaFromQasu = (size, qasu) => roundTo3(qasu / size)

const RawMaterial = (props) => {
  // Historia 170 (J4b, S103) — `preselectedRecord` additive, optional, inert when absent (every OTHER
  // caller): `RawMaterialReference.jsx` forwards this when a document repository row's "Open
  // associated record" preselects a RAW_MATERIAL inside this reference.
  const { endpoint, reference, hideColumns, onRowClick, compatibleFixtureCodes = [], showQrAction = false, preselectedRecord = null } = props
  const parallelepiped = 'PARALLELEPIPED'
  const cylindrical = 'CYLINDRICAL'
  const disableRowActions = useRef(false)
  const [editingRow, setEditingRow] = useState(null)
  const [viewMode, setViewMode] = useState('AVAILABLE')
  const forceLoad = useRef(false)
  const [toggle, resetPage] = useToggle()
  const { t_ } = useSmartTranslation()
  const [rawMaterialRow, setRawMaterialRow] = useState(null)
  const [showQr, setShowQr] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [showUsedInDialog, setShowUsedInDialog] = useState(false)
  const [selectedRawMaterialId, setSelectedRawMaterialId] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const isUserAuthorized = useAuthorization()
  const extendedColumns = useGetExtendedColumns('raw_material')
  const [forceValuesChange, setForceValuesChange] = useState(null)
  // RN4: guards the bidirectional sync against re-entrancy. When a field's
  // onChange pushes a value to its dependent (via forceValuesChange), that push
  // re-triggers the dependent's onChange; this flag makes the dependent skip its
  // own recalculation so the field the user edits stays the immutable master.
  const isSyncingRef = useRef(false)

  // Clear forceValuesChange once the table has consumed it. Otherwise the value
  // lingers and the table re-applies it on every later data change (e.g. when a
  // failed save against /rawMaterials refreshes the grid out of edit mode),
  // which would try to write into a non-existent editing row and crash the view.
  useEffect(() => {
    if (forceValuesChange) {
      setForceValuesChange(null)
    }
  }, [forceValuesChange])

  // Status filter shown in the toolbar. Keys are the RawMaterialStatus values
  // (plus ALL) sent to the backend; values are the labels shown in the select.
  const statusFilterLabels = {
    PENDING: t_('PENDING'),
    AVAILABLE: t_('AVAILABLE'),
    PROCESSED: t_('PROCESSED'),
    REJECTED: t_('REJECTED'),
    INCIDENCE: t_('INCIDENCE'),
    ALL: t_('ALL')
  }

  const columns = [{
    name: t_('Code'),
    field: 'code',
    readOnly: true,
    charLimit: 60,
    mandatory: true,
    unique: true
  },
  {
    name: t_('Name'),
    field: 'name',
    charLimit: 80,
    mandatory: true
  },
  {
    // Historia 226 (S144, R7b): a title that can be printed on a label is a t_() key in the 8 language files.
    name: t_('type'),
    field: 'type.code',
    type: 'select',
    defaultNewValue: { code: 'RAW', id: 1 },
    edit: {
      source: async () => {
        return await apiGet('rawMaterialReferenceTypes')
      },
      id: 'id',
      field: 'code'
    },
    mandatory: true
  },
  {
    name: t_('Description'),
    field: 'description',
    charLimit: 200
  },
  {
    name: t_('Status'),
    field: 'status',
    mandatory: true,
    type: 'smartSelect',
    isSimpleData: true,
    edit: {
      source: async () => {
        return await apiGet('rawMaterials/statuses')
      }
    }
  },
  {
    name: t_('X Length (mm)'),
    field: 'lengthX',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, parallelepiped)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    numeric: true,
    hide: !(reference.shape && reference.shape.code === 'PARALLELEPIPED')
  },
  {
    name: t_('Y Length (mm)'),
    field: 'lengthY',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, parallelepiped)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    numeric: true,
    hide: !(reference.shape && reference.shape.code === 'PARALLELEPIPED')
  },
  {
    name: t_('Z Length (mm)'),
    field: 'lengthZ',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, parallelepiped)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, parallelepiped)
    },
    numeric: true,
    hide: !(reference.shape && reference.shape.code === 'PARALLELEPIPED')
  },
  {
    name: t_('Diam (mm)'),
    field: 'diameter',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, cylindrical)
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, cylindrical)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, cylindrical)
    },
    numeric: true,
    hide: !(reference.shape && reference.shape.code === 'CYLINDRICAL')
  },
  {
    name: t_('Length (mm)'),
    field: 'length',
    readOnly: (rowData) => {
      return readOnlyCondition(rowData, cylindrical)
    },
    mandatory: (rowData) => {
      return mandatoryCondition(rowData, cylindrical)
    },
    reset: (rowData) => {
      return readOnlyCondition(rowData, cylindrical)
    },
    numeric: {
      default: 0
    },
    hide: !(reference.shape && reference.shape.code === 'CYLINDRICAL')
  },
  {
    name: t_('Location'),
    field: 'location.name',
    readOnly: true
  },
  {
    name: t_('Shelf'),
    field: 'shelveStorageConfiguration.shelve',
    readOnly: true,
    hide: hideColumns,
    sortable: false,
    search: false
  },
  {
    name: t_('Row'),
    field: 'shelveStoragePosition.position',
    readOnly: true,
    hide: hideColumns,
    sortable: false,
    search: false
  },
  {
    name: t_('Column'),
    field: 'shelveStoragePosition.depth',
    readOnly: true,
    hide: hideColumns,
    sortable: false,
    search: false
  },
  {
    name: t_('Fixture'),
    field: 'fixture.code',
    type: 'select',
    edit: {
      source: async () => {
        if (editingRow !== null) { return await apiGet('fixturesAvailableByRawMaterial/' + editingRow.id) }
      },
      id: 'id',
      field: 'code'
    }
  },
  {
    name: t_('Assigned'),
    field: 'part.code',
    readOnly: true
  },
  {
    name: t_('Size'),
    field: 'size',
    numeric: true,
    decimals: 2,
    // S9 / RN3: size is editable only when ref is fractionable
    readOnly: (row) => !row.rawMaterialReference.fractionable,
    // S21 / D5: reject negatives
    condition: (row) => {
      if (Number(row.size) < 0) {
        return t_('Must be positive')
      }
    },
    // S14 / RN4: changing size keeps qa and recalculates qASU = size * qa.
    // The optional second argument allows injecting a setter in unit tests.
    onChange: (row, setFVC = setForceValuesChange) => {
      if (isSyncingRef.current) { isSyncingRef.current = false; return }
      if (!row.rawMaterialReference.fractionable) return
      isSyncingRef.current = true
      setFVC({
        rowsToChange: [{
          'smart-table-index': row['smart-table-index'],
          valuesToChange: [{ field: 'quantityAvailableSecondUnit', value: computeQasuFromQa(Number(row.size), Number(row.quantityAvailable)) }]
        }]
      })
    }
  },
  {
    name: t_('Entry date'),
    field: 'entryDate',
    type: 'dateTimePicker',
    readOnly: false
  },
  {
    name: t_('Order'),
    field: 'orderNumber',
    readOnly: true
  },
  {
    name: t_('Line order number'),
    field: 'lineOrderNumber',
    readOnly: true
  },
  {
    name: t_('Delivery note number'),
    field: 'deliveryNoteNumber',
    readOnly: true
  },
  {
    name: t_('Record code'),
    field: 'recordCode',
    readOnly: true
  },
  {
    name: t_('Record position'),
    field: 'recordPosition',
    readOnly: true
  },
  {
    name: t_('Batch'),
    field: 'batch',
    // Historia 126 (MIC-4271): the batch of a hand-made instance can be corrected; the batch of an
    // instance that came from a material reception cannot. The delivery note is the ONLY criterion
    // (PO: "sin más controles"). UX prevention only -- Sun enforces the same rule on update.
    readOnly: (row) => row.deliveryNoteNumber != null
  },
  {
    name: t_('Storehouse'),
    field: 'storehouse',
    readOnly: true
  },
  {
    name: t_('Total quantity'),
    field: 'totalQuantity',
    readOnly: (row) => {
      return row.rawMaterialReference.unitary
    },
    // #76 / RN1+RN3+D-e: a fractionable non-unitary reference measures continuously, so its
    // per-instance total quantity admits decimals (scale 3). Rendering it numeric turns the
    // shared cell into an <input type="number">, which emits a dot-decimal payload the backend
    // Double parses — a plain text field let a locale comma ("1,45") through and Sun rejected it
    // with a 400. Non-fractionable (or unitary) references keep the previous integer/read-only
    // behavior unchanged (numeric is false, so the plain text field is used).
    numeric: (row) => row.rawMaterialReference.fractionable && !row.rawMaterialReference.unitary,
    decimals: 3
  },
  {
    name: t_('Quantity available'),
    field: 'quantityAvailable',
    numeric: true,
    decimals: 3,
    // S6 / RN2: a unitary ref caps qa at 1; when also fractionable it admits any
    // decimal in [0, 1], otherwise only 0 or 1. S21 / D5: reject negatives.
    // Inputs are stored as strings, so coerce before comparing (avoids '1' !== 1).
    condition: (row) => {
      // qa is required: an empty/cleared value is invalid (Number('') would be 0
      // and slip past the numeric checks below).
      if (isBlank(row.quantityAvailable)) {
        return t_('Required *')
      }
      const qa = Number(row.quantityAvailable)
      if (qa < 0) {
        return t_('Must be positive')
      }
      if (row.rawMaterialReference.unitary) {
        if (row.rawMaterialReference.fractionable) {
          if (qa > 1) {
            return t_('In a unitary reference, quantity available cannot exceed 1')
          }
        } else if (qa !== 0 && qa !== 1) {
          return t_('In a unitary reference, quantity available can only be 0 or 1')
        }
      }
    },
    // S12 / RN4: changing qa recalculates qASU = size * qa.
    // The optional second argument allows injecting a setter in unit tests.
    onChange: (row, setFVC = setForceValuesChange) => {
      if (isSyncingRef.current) { isSyncingRef.current = false; return }
      // Don't propagate from a blank source: it would push a spurious 0 and the
      // sync round-trip would clear the synchronously-set Required error (blink).
      if (isBlank(row.quantityAvailable)) return
      if (row.rawMaterialReference.unitary && row.quantityAvailable > 1) return
      if (!row.rawMaterialReference.fractionable) return
      isSyncingRef.current = true
      setFVC({
        rowsToChange: [{
          'smart-table-index': row['smart-table-index'],
          valuesToChange: [{ field: 'quantityAvailableSecondUnit', value: computeQasuFromQa(Number(row.size), Number(row.quantityAvailable)) }]
        }]
      })
    }
  },
  {
    name: t_('Quantity available second unit'),
    field: 'quantityAvailableSecondUnit',
    numeric: true,
    decimals: 3,
    // S9 / RN3: readOnly when NOT fractionable (fix D7: was inverted).
    // S17 / D1: also readOnly when fractionable but size<=0 (avoid division by zero).
    readOnly: (row) => !row.rawMaterialReference.fractionable || !(row.size > 0),
    // S21 / D5: reject negatives. RN2: editing qASU drives qa = qASU / size, and
    // qa is recalculated through the async forceValuesChange round-trip, where the
    // dependent field's own condition does not surface reliably. So enforce the
    // unitary cap (qa <= 1) here, on the field actually being edited, so the error
    // fires synchronously.
    condition: (row) => {
      // When fractionable, qASU is editable and required: a blank value is invalid.
      if (row.rawMaterialReference.fractionable && isBlank(row.quantityAvailableSecondUnit)) {
        return t_('Required *')
      }
      if (Number(row.quantityAvailableSecondUnit) < 0) {
        return t_('Must be positive')
      }
      if (row.rawMaterialReference.unitary && Number(row.size) > 0) {
        if (computeQaFromQasu(Number(row.size), Number(row.quantityAvailableSecondUnit)) > 1) {
          return t_('In a unitary reference, quantity available cannot exceed 1')
        }
      }
    },
    // S13 / RN4: changing qASU recalculates qa = qasu / size (guard: size > 0).
    // The optional second argument allows injecting a setter in unit tests.
    onChange: (row, setFVC = setForceValuesChange) => {
      if (isSyncingRef.current) { isSyncingRef.current = false; return }
      // Don't propagate from a blank source: it would push a spurious 0 and the
      // sync round-trip would clear the synchronously-set Required error (blink).
      if (isBlank(row.quantityAvailableSecondUnit)) return
      if (!row.rawMaterialReference.fractionable) return
      if (!(Number(row.size) > 0)) return
      // RN2: do not propagate a qa that would exceed 1 in a unitary ref. The qASU
      // condition already flags it; pushing it would re-enter changeTextValue and
      // clear that synchronously-set error (the "blink"). Mirrors qa's own guard.
      if (row.rawMaterialReference.unitary && computeQaFromQasu(Number(row.size), Number(row.quantityAvailableSecondUnit)) > 1) return
      isSyncingRef.current = true
      setFVC({
        rowsToChange: [{
          'smart-table-index': row['smart-table-index'],
          valuesToChange: [{ field: 'quantityAvailable', value: computeQaFromQasu(Number(row.size), Number(row.quantityAvailableSecondUnit)) }]
        }]
      })
    }
  }
  ]

  const getColumns = () => {
    if (extendedColumns?.length === 0) {
      return columns
    }
    return columns.concat(extendedColumns)
  }

  const toggleAssigned = (value) => {
    if (!value) {
      return
    }
    setViewMode(value)
    forceLoad.current = !forceLoad.current
    resetPage()
  }

  const mandatoryCondition = (row, property) => {
    if (!reference.shape) {
      return false
    }
    if (reference.shape.code === property) return true
    return false
  }

  const readOnlyCondition = (row, property) => {
    if (
      reference.shape === undefined ||
      reference.shape === null ||
      reference.shape.code !== property ||
      reference.shape.code === '' ||
      reference.shape.code === undefined
    ) {
      return true
    }
    return false
  }

  const actions = {
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: editingRow || !isUserAuthorized('DOCUMENTS_RAW_MATERIAL'),
        onClick: (row) => {
          setRawMaterialRow(row)
          setShowDocument(true)
        }
      },
      {
        name: 'Used in',
        render: (row) => {
          return (
            <IconButton>
              <SearchIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          setSelectedRawMaterialId(row.id)
          setShowUsedInDialog(true)
        }

      },
      ...(showQrAction
        ? [{
            name: 'Generate QR',
            render: (row) => (
              <IconButton>
                <QrCode2Icon />
              </IconButton>
            ),
            disabled: editingRow || !isUserAuthorized('GENERATE_QR_CODE'),
            onClick: (row) => {
              setRawMaterialRow(row)
              setShowQr(true)
            }
          }]
        : [])
    ],
    edit: {
      fetchFunction: (row) => {
        return apiPut('rawMaterials', row)
      },
      disabled: !isUserAuthorized('EDIT_RAW_MATERIAL')
    },
    delete: {
      disabled: !isUserAuthorized('DELETE_RAW_MATERIAL'),
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('rawMaterials/' + rows[0].id, rows)
        } else {
          return apiDelete('rawMaterialsMulti', rows)
        }
      }
    }
  }

  const toolbar = {
    render: {
      fetchFunction: (extraFunctions) => {
        return (
          <SmartSelect
            label={t_('Status')}
            selectableOptions={Object.keys(statusFilterLabels)}
            optionLabelTransform={(option) => statusFilterLabels[option] || String(option)}
            value={viewMode}
            onChange={(value) => toggleAssigned(value)}
            sortValues={false}
            size='small'
            width='15rem'
          />
        )
      }
    },
    renderSticky: {
      fetchFunction: (extraFunctions) => {
        const handleAlert = extraFunctions.showAlert
        return (
          <AddRawMaterialElement
            disable={!isUserAuthorized('ADD_RAW_MATERIAL')}
            row={reference}
            onAlert={handleAlert}
            title={t_('Add raw materials')}
          />
        )
      }
    }
  }

  const onEditIndexChanged = (editIndex, row) => {
    if (editIndex >= 0) {
      setEditingRow(row)
    } else {
      setEditingRow(null)
    }
  }

  const dataFetch = async (pagination) => {
    const rawMaterials = await apiGet(endpoint + '/' + reference.id + '/' + viewMode.replaceAll(' ', '_'), pagination)
    if (compatibleFixtureCodes.length > 0) {
      rawMaterials.content = rawMaterials.content.map(rawMaterial => ({
        ...rawMaterial,
        disabled: !compatibleFixtureCodes.includes(rawMaterial.fixtureReference?.code)
      }))
    }
    return rawMaterials
  }

  if (extendedColumns === null || extendedColumns === undefined) {
    return <CircularProgress />
  }

  return (
    <div>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={<Document reference={rawMaterialRow} resourceType='RAW_MATERIAL' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <SmartDialog
        title={createDialogTitle({ title: t_('Used in'), icon: TextSnippetOutlined })}
        closeCallback={() => setShowUsedInDialog(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={
          // SAT #4009 (sun) / #3141: one row per consumption of this instance, with the part, its order,
          // the job and asset that consumed it, how much and when. A partially consumed instance stays
          // AVAILABLE, so this is where its use shows. A new tune key: a tune saved for the old three
          // columns must not hide the new ones.
          <SmartMateriaUITable
            tableTuneUniqueKey='rawMaterialConsumptions'
            columns={[
              { name: t_('Part'), field: 'part.code' },
              { name: t_('Name'), field: 'part.name' },
              { name: t_('FO'), field: 'fabricationOrder.code' },
              { name: t_('Job'), field: 'job', render: (row) => <span>{row.job ? t_(row.job) : ''}</span> },
              { name: t_('Asset'), field: 'deviceName' },
              { name: t_('Quantity'), field: 'quantity', numeric: true },
              { name: t_('Quantity second unit'), field: 'secondUnitQuantity', numeric: true, sortable: false, search: false },
              { name: t_('Unit'), field: 'secondUnit', sortable: false, search: false },
              { name: t_('Date'), field: 'date', type: 'dateTimePicker' }
            ]}
            dataFetch={async (pagination) => {
              if (selectedRawMaterialId) {
                return await apiGet(`rawMaterialConsumption/${selectedRawMaterialId}`, pagination)
              }
              return { content: [], totalElements: 0 }
            }}
            pageable
            sortOptions={{ default: { field: 'date', direction: -1 } }}
            sticky={false}
          />
        }
        setOpen={showUsedInDialog}
        paperWidth='80vw'
      />
      {showQr && (
        <QrCodeGeneratorDialog
          open={showQr}
          onClose={() => setShowQr(false)}
          entityType='RawMaterial'
          entityLabel={t_('Raw Material')}
          instance={rawMaterialRow}
          availableFields={getColumns()
            .filter(c => c.field)
            .map(c => ({ field: c.field, label: c.name, type: c.type }))}
        />
      )}
      <SmartMateriaUITable
        tableTuneUniqueKey='rawMaterial'
        columns={getColumns()}
        title={reference.name}
        dataFetch={dataFetch}
        actions={actions}
        preselectedRecord={preselectedRecord}
        multipleSelection={!hideColumns}
        deleteMultipleSelectionDisabled={!isUserAuthorized('DELETE_RAW_MATERIAL')}
        toolbar={toolbar}
        forceLoad={forceLoad.current}
        forceValuesChange={forceValuesChange}
        setDisableRowActions={(event) => { disableRowActions.current = event }}
        onEditIndexChanged={onEditIndexChanged}
        resetPage={toggle}
        onRowClick={(row) => {
          if (onRowClick && typeof onRowClick === 'function' && row.id) {
            onRowClick(row)
          }
        }}
        extraFilterParameter={{ name: 'disabled', value: true }}
        extraFilterParameterMessage={t_('The fixture assigned to this raw material is not compatible.')}
        pageable
      />
    </div>
  )
}

export default RawMaterial
